// src/hooks/apiKeys/useApiKeys.tsx

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { toastErrorUnlessHandled } from "@/hooks/useAxiosPrivate";
import {
  getApiKeys,
  getOrgApiKeys,
  createApiKey,
  updateApiKey,
  revokeApiKey,
  getApiKeyStats,
} from "@/api/apiKeys";
import type {
  ApiKey,
  ApiKeyCreateRequest,
  ApiKeyStats,
  ApiKeyUpdateRequest,
  ApiKeyRecentUsage,
} from "@/types/apiKeys";

interface UseApiKeysOptions {
  orgId?: number;
  autoLoad?: boolean;
}

export const useApiKeys = (options: UseApiKeysOptions = {}) => {
  const { orgId, autoLoad = true } = options;
  
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [stats, setStats] = useState<ApiKeyStats | null>(null);
  const [recentUsage, setRecentUsage] = useState<ApiKeyRecentUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Fetch API keys
  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const response = orgId
        ? await getOrgApiKeys(orgId, controller.signal)
        : await getApiKeys(undefined, controller.signal);
      
      setKeys(response.data);
      
      return () => controller.abort();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to fetch API keys";
      setError(errorMessage);
      toastErrorUnlessHandled(err, errorMessage);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const controller = new AbortController();
      const response = await getApiKeyStats(orgId, controller.signal);
      
      setStats(response.stats);
      setRecentUsage(response.recent_usage);
      
      return () => controller.abort();
    } catch (err: any) {
      console.error("Failed to fetch API key statistics:", err);
    }
  }, [orgId]);

  // Create new API key
  const handleCreateKey = useCallback(async (data: ApiKeyCreateRequest) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await createApiKey(data);
      
      // Store the full key temporarily (will be shown once)
      setCreatedKey(response.data.key);
      
      // Refresh the keys list
      await fetchKeys();
      
      toast.success(response.message);
      
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to create API key";
      setError(errorMessage);
      toastErrorUnlessHandled(err, errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchKeys]);

  // Update API key
  const handleUpdateKey = useCallback(async (id: number, data: ApiKeyUpdateRequest) => {
    if (data.is_active === false) {
      const isConfirmed = window.confirm(
        "Είστε σίγουροι ότι θέλετε να παύσετε αυτό το API key;"
      );

      if (!isConfirmed) return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await updateApiKey(id, data);
      
      // Update the key in the local state
      setKeys(prevKeys => 
        prevKeys.map(key => 
          key.id === id 
            ? { ...key, ...response.data }
            : key
        )
      );
      
      toast.success(response.message);
      
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to update API key";
      setError(errorMessage);
      toastErrorUnlessHandled(err, errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Revoke (delete) API key
  const handleRevokeKey = useCallback(async (id: number) => {
    const isConfirmed = window.confirm(
      "ΠΡΟΣΟΧΗ: Είστε σίγουροι ότι θέλετε να ανακαλέσετε αυτό το API key; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί."
    );
    
    if (!isConfirmed) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await revokeApiKey(id);
      
      // Remove the key from local state
      setKeys(prevKeys => prevKeys.filter(key => key.id !== id));
      
      // Refresh stats
      await fetchStats();
      
      toast.success(response.message);
      
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to revoke API key";
      setError(errorMessage);
      toastErrorUnlessHandled(err, errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  // Toggle API key active status
  const handleToggleKeyStatus = useCallback(async (id: number, isActive: boolean) => {
    return handleUpdateKey(id, { is_active: !isActive });
  }, [handleUpdateKey]);

  // Clear the created key from memory (after user has copied it)
  const clearCreatedKey = useCallback(() => {
    setCreatedKey(null);
  }, []);

  // Auto-load data on mount if enabled
  useEffect(() => {
    if (autoLoad) {
      fetchKeys();
      fetchStats();
    }
  }, [autoLoad, fetchKeys, fetchStats]);

  return {
    keys,
    stats,
    recentUsage,
    loading,
    error,
    createdKey,
    fetchKeys,
    fetchStats,
    createKey: handleCreateKey,
    updateKey: handleUpdateKey,
    revokeKey: handleRevokeKey,
    toggleKeyStatus: handleToggleKeyStatus,
    clearCreatedKey,
  };
};
